"use client";

import { useCallback, useRef, useState } from "react";

import DNCConfirmationModal from "@/app/(authenticated)/leads/_modules/components/DNCConfirmationModal";

/**
 * DNC on the dialers asks first, with the Leads page's own dialog — including "Also block
 * their calls and texts to me" (on by default, undo in Settings → Blocked Numbers).
 *
 * `await confirmDnc()` → the block choice, or null if they backed out. Take the lead id
 * BEFORE awaiting: a dialer can move to the next lead while the dialog is open.
 */
export function useDncConfirm() {
  const [open, setOpen] = useState(false);
  const answer = useRef<((blockInbound: boolean | null) => void) | null>(null);

  const settle = useCallback((blockInbound: boolean | null) => {
    answer.current?.(blockInbound);
    answer.current = null;
    setOpen(false);
  }, []);

  const confirmDnc = useCallback(() => {
    answer.current?.(null); // a second click replaces the first ask
    setOpen(true);
    return new Promise<boolean | null>((resolve) => {
      answer.current = resolve;
    });
  }, []);

  // Mounted only while open so the checkbox starts ticked every time.
  const modal = open ? (
    <DNCConfirmationModal
      isOpen
      onOpenChange={(isOpen) => !isOpen && settle(null)}
      onRecover={() => Promise.resolve()}
      onConfirm={(blockInbound) => {
        settle(blockInbound === true);
        return Promise.resolve();
      }}
    />
  ) : null;

  return { confirmDnc, modal };
}
